import posthog from 'posthog-js';
import { getCommonProperties } from './common.helper';

// Track timing of first and last results
let firstResultTime = null;
let lastResultTime = null;
let startTime = null;

// Consent tracking metrics
let consentViewStartTime = null;

// Consent Renewal tracking metrics
let consentRenewalViewStartTime = null;

// Internal function to reset timing metrics
const resetTimingMetrics = () => {
	firstResultTime = null;
	lastResultTime = null;
	startTime = null;
};

// Internal function to update timing metrics
const updateTimingMetrics = (isFirstResult = false) => {
	const currentTime = Date.now();

	if (!startTime) {
		startTime = currentTime;
	}

	if (isFirstResult && !firstResultTime) {
		firstResultTime = currentTime;
	}

	lastResultTime = currentTime;
};

// Central method to handle analytics enabled check and common functionality
const captureEvent = (event, properties) => {
	if (!global?.sdkOptions?.analyticsEnabled) return;

	posthog.capture(event, {
		...properties,
		...getCommonProperties(),
		entityId: global?.entityId,
	});
};

const captureDiscoveryCompleted = (fipId, metrics) => {
	const isFirstResult = !firstResultTime;
	updateTimingMetrics(isFirstResult);

	captureEvent('fip_discovery_completed', {
		fipId,
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});
};

const captureDiscoveryError = (fipId, metrics) => {
	const isFirstResult = !firstResultTime;
	updateTimingMetrics(isFirstResult);

	captureEvent('fip_discovery_error', {
		fipId,
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});
};

const captureDiscoveryRecordNotFound = (fipId, metrics) => {
	const isFirstResult = !firstResultTime;
	updateTimingMetrics(isFirstResult);

	captureEvent('fip_record_not_found', {
		fipId,
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});
};

const captureAllDiscoveriesCompleted = (metrics) => {
	captureEvent('all_fip_discoveries_completed', {
		...metrics,
		timeToFirstResult: firstResultTime ? firstResultTime - startTime : null,
		timeToLastResult: lastResultTime ? lastResultTime - startTime : null,
	});

	// Reset timing metrics after completion
	resetTimingMetrics();
};

// OTP Analytics Functions
const captureOtpPageLoad = (metrics) => {
	captureEvent('otp_page_loaded', {
		...metrics,
	});
};

const captureOtpInputTriggered = (metrics) => {
	captureEvent('otp_input_triggered', {
		...metrics,
	});
};

const captureOtpInputStart = (metrics) => {
	captureEvent('otp_input_started', {
		...metrics,
	});
};

// OTP Progress Tracking Configuration
const OTP_PROGRESS_CONFIG = {
	trackAtHalfway: true, // Track at 50% completion
	trackAtQuarter: false, // Track at 25% completion
	totalDigits: 6, // Total number of digits in OTP
};

// Helper function to calculate OTP progress metrics
const calculateOtpProgressMetrics = (
	currentLength,
	lastInputTime,
	pageLoadTime,
	correctionCount
) => {
	const currentTime = performance.now();
	const timePerDigit = lastInputTime
		? currentTime - lastInputTime
		: currentTime - pageLoadTime;

	return {
		digitsEntered: currentLength,
		timePerDigit,
		corrections: correctionCount,
		completionPercentage:
			(currentLength / OTP_PROGRESS_CONFIG.totalDigits) * 100,
		timeSinceStart: currentTime - pageLoadTime,
	};
};

// Helper function to check if we should track progress
const shouldTrackOtpProgress = (currentLength) => {
	if (
		OTP_PROGRESS_CONFIG.trackAtHalfway &&
		currentLength === Math.ceil(OTP_PROGRESS_CONFIG.totalDigits / 2)
	) {
		return true;
	}

	if (
		OTP_PROGRESS_CONFIG.trackAtQuarter &&
		currentLength === Math.ceil(OTP_PROGRESS_CONFIG.totalDigits / 4)
	) {
		return true;
	}

	return false;
};

const captureOtpInputProgress = (metrics) => {
	captureEvent('otp_input_progress', {
		...metrics,
	});
};

const captureOtpVerificationAttempt = (metrics) => {
	captureEvent('otp_verification_attempt', {
		...metrics,
	});
};

const captureOtpVerificationResult = (metrics) => {
	captureEvent('otp_verification_result', {
		...metrics,
	});
};

const startConsentView = () => {
	consentViewStartTime = Date.now();
};

const captureConsentApproved = (metrics) => {
	const timeToApprove = consentViewStartTime
		? Date.now() - consentViewStartTime
		: null;

	captureEvent('consent_approved', {
		...metrics,
		timeToApprove,
	});

	// Reset metrics
	consentViewStartTime = null;
};

const captureConsentDenied = (metrics) => {
	const timeToDeny = consentViewStartTime
		? Date.now() - consentViewStartTime
		: null;

	captureEvent('consent_denied', {
		...metrics,
		timeToDeny,
	});

	// Reset metrics
	consentViewStartTime = null;
};

// Track consent page load with account blocking metrics
const captureConsentPageLoaded = (metrics) => {
	captureEvent('consent_page_loaded', {
		...metrics,
	});
};

// Consent Renewal Analytics Functions
const startConsentRenewalView = () => {
	consentRenewalViewStartTime = Date.now();
};

const captureConsentRenewalPageLoaded = (metrics) => {
	captureEvent('consent_renewal_page_loaded', {
		...metrics,
	});
};

const captureConsentRenewalApproved = (metrics) => {
	const timeToApprove = consentRenewalViewStartTime
		? Date.now() - consentRenewalViewStartTime
		: null;

	captureEvent('consent_renewal_approved', {
		...metrics,
		timeToApprove,
	});

	// Reset metrics
	consentRenewalViewStartTime = null;
};

const captureConsentRenewalDenied = (metrics) => {
	const timeToDeny = consentRenewalViewStartTime
		? Date.now() - consentRenewalViewStartTime
		: null;

	captureEvent('consent_renewal_denied', {
		...metrics,
		timeToDeny,
	});

	// Reset metrics
	consentRenewalViewStartTime = null;
};

// Consent Renewal OTP Analytics Functions (mirroring login OTP flow)
const captureConsentRenewalOtpPageLoad = (metrics) => {
	captureEvent('consent_renewal_otp_page_loaded', {
		...metrics,
	});
};

const captureConsentRenewalOtpInputTriggered = (metrics) => {
	captureEvent('consent_renewal_otp_input_triggered', {
		...metrics,
	});
};

const captureConsentRenewalOtpInputStart = (metrics) => {
	captureEvent('consent_renewal_otp_input_started', {
		...metrics,
	});
};

const captureConsentRenewalOtpInputProgress = (metrics) => {
	captureEvent('consent_renewal_otp_input_progress', {
		...metrics,
	});
};

const captureConsentRenewalOtpVerificationAttempt = (metrics) => {
	captureEvent('consent_renewal_otp_verification_attempt', {
		...metrics,
	});
};

const captureConsentRenewalOtpVerificationResult = (metrics) => {
	captureEvent('consent_renewal_otp_verification_result', {
		...metrics,
	});
};

// ==========================================
// MFA (Multi-Factor Authentication) Analytics
// ==========================================

// MFA Funnel & Step Lifecycle
const captureMfaFlowStarted = (metrics) => {
	captureEvent('mfa_flow_started', {
		...metrics,
	});
};

const captureMfaStepViewed = (metrics) => {
	captureEvent('mfa_step_viewed', {
		...metrics,
	});
};

const captureMfaFlowCompleted = (metrics) => {
	captureEvent('mfa_flow_completed', {
		...metrics,
	});
};

const captureMfaFlowFailed = (metrics) => {
	captureEvent('mfa_flow_failed', {
		...metrics,
	});
};

// Factor Chooser
const captureMfaFactorChooserViewed = (metrics) => {
	captureEvent('mfa_factor_chooser_viewed', {
		...metrics,
	});
};

const captureMfaFactorSelected = (metrics) => {
	captureEvent('mfa_factor_selected', {
		...metrics,
	});
};

// WebAuthn / Passkey Lifecycle
const captureMfaWebAuthnCeremonyStarted = (metrics) => {
	captureEvent('mfa_webauthn_ceremony_started', {
		...metrics,
	});
};

const captureMfaWebAuthnCompleted = (metrics) => {
	captureEvent('mfa_webauthn_completed', {
		...metrics,
	});
};

const captureMfaWebAuthnFailed = (metrics) => {
	captureEvent('mfa_webauthn_ceremony_failed', {
		...metrics,
	});
};

const captureMfaWebAuthnFallbackToPin = (metrics) => {
	captureEvent('mfa_webauthn_fallback_to_pin', {
		...metrics,
	});
};

// PIN Flow
const captureMfaPinSheetOpened = (metrics) => {
	captureEvent('mfa_pin_sheet_opened', {
		...metrics,
	});
};

const captureMfaPinSubmitted = (metrics) => {
	captureEvent('mfa_pin_submitted', {
		...metrics,
	});
};

const captureMfaPinResult = (metrics) => {
	captureEvent('mfa_pin_result', {
		...metrics,
	});
};

const captureMfaPinRecoveryStarted = (metrics) => {
	captureEvent('mfa_pin_recovery_started', {
		...metrics,
	});
};

const captureMfaPinRecoveryCompleted = (metrics) => {
	captureEvent('mfa_pin_recovery_completed', {
		...metrics,
	});
};

// SNA (Silent Network Auth)
const captureMfaSnaStarted = (metrics) => {
	captureEvent('mfa_sna_started', {
		...metrics,
	});
};

const captureMfaSnaCompleted = (metrics) => {
	captureEvent('mfa_sna_completed', {
		...metrics,
	});
};

const captureMfaSnaFallbackToOtp = (metrics) => {
	captureEvent('mfa_sna_fallback_to_otp', {
		...metrics,
	});
};

export {
	captureDiscoveryCompleted,
	captureDiscoveryError,
	captureAllDiscoveriesCompleted,
	captureDiscoveryRecordNotFound,
	captureOtpPageLoad,
	captureOtpInputTriggered,
	captureOtpInputStart,
	captureOtpInputProgress,
	captureOtpVerificationAttempt,
	captureOtpVerificationResult,
	calculateOtpProgressMetrics,
	shouldTrackOtpProgress,
	OTP_PROGRESS_CONFIG,
	startConsentView,
	captureConsentApproved,
	captureConsentDenied,
	captureConsentPageLoaded,
	startConsentRenewalView,
	captureConsentRenewalPageLoaded,
	captureConsentRenewalApproved,
	captureConsentRenewalDenied,
	captureConsentRenewalOtpPageLoad,
	captureConsentRenewalOtpInputTriggered,
	captureConsentRenewalOtpInputStart,
	captureConsentRenewalOtpInputProgress,
	captureConsentRenewalOtpVerificationAttempt,
	captureConsentRenewalOtpVerificationResult,
	// MFA Exports
	captureMfaFlowStarted,
	captureMfaStepViewed,
	captureMfaFlowCompleted,
	captureMfaFlowFailed,
	captureMfaFactorChooserViewed,
	captureMfaFactorSelected,
	captureMfaWebAuthnCeremonyStarted,
	captureMfaWebAuthnCompleted,
	captureMfaWebAuthnFailed,
	captureMfaWebAuthnFallbackToPin,
	captureMfaPinSheetOpened,
	captureMfaPinSubmitted,
	captureMfaPinResult,
	captureMfaPinRecoveryStarted,
	captureMfaPinRecoveryCompleted,
	captureMfaSnaStarted,
	captureMfaSnaCompleted,
	captureMfaSnaFallbackToOtp,
};
